import { useTranslation } from "react-i18next";
import { Tabs, Tab } from "@components/common/Tabs";
import { useEffect, useState, useRef } from "react";
import { Travel, Status } from "@gen/travel/v1/common_pb";
import { ConnectError } from "@connectrpc/connect";
import { useToaster } from "@hooks/useToaster";
import { getTravels } from "@services/travel";
import { PageRequest } from "@gen/tsam/v1/page_pb";
import { UserTravel } from "./UserTravel";
import { Spinner } from "@thetreep/ui";
import FiltersBookings from "./FiltersBookings";
import { CancelModal } from "./Cancel";
import { ExchangeModal } from "./ExchangeModal";
import { NoExchangeModal } from "@pages/booking/exchange/noExchangeModal";
import { localDateTimeToDate } from "@utils/date";

interface FiltersValue {
  dates: Date[] | null;
  status: number[] | undefined;
  search: string | undefined;
}

type TravelListProps = {
  travels: Travel[];
  isLoading: boolean;
  emptyMessage: string;
  onCancel?: (travel: Travel) => void;
  onExchange?: (travel: Travel) => void;
};

function TravelList({
  travels,
  isLoading,
  emptyMessage,
  onCancel,
  onExchange,
}: TravelListProps) {
  if (isLoading)
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Spinner />
      </div>
    );

  if (travels.length === 0)
    return <div className="text-base my-12 font-jakarta">{emptyMessage}</div>;

  return (
    <div className="flex flex-col gap-10 my-8">
      {travels.map((travel, index) => (
        <div key={index}>
          <UserTravel
            travel={travel}
            onCancel={onCancel ? () => onCancel(travel) : undefined}
            onExchange={onExchange ? () => onExchange(travel) : undefined}
          />
        </div>
      ))}
    </div>
  );
}

export default function UserBookings() {
  const { t } = useTranslation();
  const { addToast } = useToaster();

  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [currentTravels, setCurrentTravels] = useState<Travel[]>([]);
  const [pastTravels, setPastTravels] = useState<Travel[]>([]);
  const [selectedTravel, setSelectedTravel] = useState<Travel>();
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isExchangeOpen, setIsExchangeOpen] = useState(false);
  const [isNoExchangeOpen, setIsNoExchangeOpen] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<FiltersValue>({
    dates: null,
    status: [],
    search: undefined,
  });
  const [offlineInfo, setOfflineInfo] = useState<string>("");
  const [agencyPhoneNumber, setAgencyPhoneNumber] = useState<string>("");
  const [workHours, setWorkHours] = useState<string>("");

  const fetchTravels = async (filters: FiltersValue) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsLoading(true);

    const pageRequest: PageRequest = {
      $typeName: "tsam.v1.PageRequest",
      page: 1,
      size: 100,
    };

    try {
      const response = await getTravels({
        page: pageRequest,
        filters: {
          from: filters.dates?.[0],
          to: filters.dates?.[1],
          place: filters.search,
          statuses: filters.status,
        },
        signal: abortController.signal,
      });

      if (abortController.signal.aborted) {
        return;
      }

      const current = response.travels
        .filter(
          (travel) =>
            travel.status === Status.IN_PROGRESS ||
            travel.status === Status.UPCOMING,
        )
        .sort((a, b) => {
          if (
            a.outward?.schedule?.departure &&
            b.outward?.schedule?.departure
          ) {
            return (
              Number(localDateTimeToDate(a.outward.schedule.departure)) -
              Number(localDateTimeToDate(b.outward.schedule.departure))
            );
          }
          return 0;
        });

      const past = response.travels
        .filter(
          (travel) =>
            travel.status === Status.COMPLETED ||
            travel.status === Status.CANCELLED,
        )
        .sort((a, b) => {
          if (
            a.outward?.schedule?.departure &&
            b.outward?.schedule?.departure
          ) {
            return (
              Number(localDateTimeToDate(a.outward.schedule.departure)) -
              Number(localDateTimeToDate(b.outward.schedule.departure))
            );
          }
          return 0;
        });

      setCurrentTravels(current);
      setPastTravels(past);
    } catch (e: unknown) {
      if (abortController.signal.aborted) {
        return;
      }

      if (e instanceof ConnectError && e.rawMessage) {
        addToast("error", e.rawMessage);
      } else {
        addToast("error", t("genericError"));
      }
    } finally {
      if (!abortController.signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    fetchTravels(currentFilters);
  }, [currentFilters]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleCancel = (travel: Travel) => {
    setSelectedTravel(travel);
    setIsCancelOpen(true);
  };

  const handleExchange = (travel: Travel) => {
    setSelectedTravel(travel);

    if (travel.isExchangeable === false) {
      setIsNoExchangeOpen(true);
      setOfflineInfo(travel.agencyInfo?.alias ?? "");
      setAgencyPhoneNumber(travel.agencyInfo?.contactPhone ?? "");
      setWorkHours(travel.agencyInfo?.openingHours ?? "");
    } else {
      setIsExchangeOpen(true);
    }
  };

  return (
    <>
      <div className="w-full pt-12 md:pt-20 pb-6 flex flex-col gap-10 px-5 mx-auto min-h-[600px]">
        <div className="font-jakarta text-[28px] md:text-[32px] font-bold">
          {t("userBookings.title")}
        </div>

        <Tabs>
          <Tab title={t("userBookings.currentTravels")}>
            <FiltersBookings onChange={setCurrentFilters} />

            <TravelList
              isLoading={isLoading}
              travels={currentTravels}
              emptyMessage={t("userBookings.noCurrentTravels")}
              onCancel={handleCancel}
              onExchange={handleExchange}
            />
          </Tab>

          <Tab title={t("userBookings.pastTravels")}>
            <FiltersBookings isCompleted onChange={setCurrentFilters} />

            <TravelList
              isLoading={isLoading}
              travels={pastTravels}
              emptyMessage={t("userBookings.noPastTravels")}
            />
          </Tab>
        </Tabs>
      </div>

      <CancelModal
        isOpen={isCancelOpen}
        onClose={() => setIsCancelOpen(false)}
        onSuccess={() => {
          setIsCancelOpen(false);
          fetchTravels(currentFilters);
        }}
        travel={selectedTravel}
      />

      <ExchangeModal
        isOpen={isExchangeOpen}
        onClose={() => setIsExchangeOpen(false)}
        travel={selectedTravel}
      />

      <NoExchangeModal
        isOpen={isNoExchangeOpen}
        onClose={() => setIsNoExchangeOpen(false)}
        travel={selectedTravel}
        agencyName={offlineInfo}
        workingHour={workHours}
        phoneNumber={agencyPhoneNumber}
      />
    </>
  );
}
